"use client"

import * as React from "react"
import { Clock } from "lucide-react"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Card, CardContent, CardFooter } from "./card"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { Sonner } from "./toast"

export interface MedicationPreference {
  id: string
  medication_name: string
  preferred_dosage: string
  status: 'approved' | 'pending' | 'denied'
  treatment_type?: string
}

export interface Provider {
  id: string
  name: string
  specialty: string
  profile_id: string
  treatment_type?: string
  is_primary?: boolean
}

export interface AppointmentSlot {
  slot_date: string
  slot_start_time: string
  slot_end_time: string
  duration_minutes: number
  // Timezone-aware display properties (from getAvailableSlots)
  display_start_time?: string
  display_end_time?: string
  display_timezone?: string
  formatted_start_time?: string
  formatted_end_time?: string
  provider_timezone?: string
}

export interface ExistingAppointment {
  id: string
  providerId: string
  providerName: string
  appointmentDate: string
  formattedDate?: string
  startTime: string
  treatmentType: string
  appointmentType: string
}

export interface VisitsBookingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  patientProfileId: string
  medicationPreferences: MedicationPreference[]
  providers: Provider[]
  onBookAppointment: (appointmentData: {
    patientProfileId: string
    providerId: string
    appointmentDate: string
    startTime: string
    treatmentType: string
    medicationPreferenceId: string
    appointmentType: string
    patientNotes?: string
  }) => Promise<{ success: boolean; message?: string; appointmentId?: string }>
  onGetAvailableSlots: (
    providerId: string,
    startDate: string,
    endDate: string,
    treatmentType?: string,
    patientTimezone?: string
  ) => Promise<{ data: AppointmentSlot[]; error?: any }>
  // Reschedule mode props
  isRescheduleMode?: boolean
  existingAppointment?: ExistingAppointment
  onRescheduleAppointment?: (appointmentData: {
    appointmentId: string
    appointmentDate: string
    startTime: string
  }) => Promise<{ success: boolean; message?: string }>
}

export function VisitsBookingDialog({
  open,
  onOpenChange,
  patientProfileId,
  medicationPreferences = [],
  providers = [],
  onBookAppointment,
  onGetAvailableSlots,
  isRescheduleMode = false,
  existingAppointment,
  onRescheduleAppointment,
}: VisitsBookingDialogProps) {
  // Debug logging
  React.useEffect(() => {
    console.log('🚀 TIMEZONE FIX 2025-10-29-00:20: Enhanced visits-booking-dialog with timezone support loaded!')
    console.log('🔍 VisitsBookingDialog props changed:', {
      open,
      patientProfileId,
      medicationPreferences: medicationPreferences.length,
      providers: providers.length,
      isRescheduleMode,
      existingAppointment
    })
  }, [open, patientProfileId, medicationPreferences, providers, isRescheduleMode, existingAppointment])

  // Update date and time when reschedule mode changes
  React.useEffect(() => {
    if (isRescheduleMode && existingAppointment) {
      console.log('🔍 Setting up reschedule mode with appointment:', existingAppointment)
      
      // Parse and set the appointment date (avoid timezone issues)
      const [year, month, day] = existingAppointment.appointmentDate.split('-').map(Number)
      const appointmentDate = new Date(year, month - 1, day) // month is 0-indexed
      console.log('🔍 Setting date to:', appointmentDate, 'from:', existingAppointment.appointmentDate)
      setDate(appointmentDate)
      setCalendarMonth(new Date(year, month - 1, 1)) // Also set calendar month
      
      // Parse and set the appointment time - ensure it persists
      const appointmentTime = existingAppointment.startTime.slice(0, 5) // Convert HH:MM:SS to HH:MM
      console.log('🔍 Setting time to:', appointmentTime, 'from:', existingAppointment.startTime)
      setSelectedTime(appointmentTime)
      
      // Set the provider selection (though it's hidden in reschedule mode)
      const providerSelection = `${existingAppointment.providerId}-${existingAppointment.treatmentType}`
      console.log('🔍 Setting provider selection to:', providerSelection)
      setSelectedMedicationProvider(providerSelection)
    } else if (!isRescheduleMode) {
      // Reset to defaults when not in reschedule mode
      setDate(new Date())
      setSelectedTime(null)
      setSelectedMedicationProvider("")
    }
  }, [isRescheduleMode, existingAppointment])
  const [date, setDate] = React.useState<Date | undefined>(() => {
    if (isRescheduleMode && existingAppointment) {
      const [year, month, day] = existingAppointment.appointmentDate.split('-').map(Number)
      return new Date(year, month - 1, day) // month is 0-indexed
    }
    return new Date()
  })
  const [calendarMonth, setCalendarMonth] = React.useState<Date>(() => {
    if (isRescheduleMode && existingAppointment) {
      const [year, month] = existingAppointment.appointmentDate.split('-').map(Number)
      return new Date(year, month - 1, 1) // First day of the month for calendar display
    }
    return new Date()
  })
  const [selectedTime, setSelectedTime] = React.useState<string | null>(
    isRescheduleMode && existingAppointment 
      ? existingAppointment.startTime.slice(0, 5) // Convert HH:MM:SS to HH:MM
      : null
  )
  const [selectedMedicationProvider, setSelectedMedicationProvider] = React.useState<string>(
    isRescheduleMode && existingAppointment 
      ? `${existingAppointment.providerId}-${existingAppointment.treatmentType}`
      : ""
  )
  const [availableSlots, setAvailableSlots] = React.useState<AppointmentSlot[]>([])
  const [bookedDates, setBookedDates] = React.useState<Date[]>([])
  const [loading, setLoading] = React.useState(false)
  const [isBooking, setIsBooking] = React.useState(false)
  
  // Timezone state - detect user's timezone
  const [patientTimezone] = React.useState(() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
      console.log(`🌍 TIMEZONE DETECTION: Detected timezone: ${detected}`)
      console.log(`🌍 CURRENT TIME DEBUG: Browser time: ${new Date().toString()}`)
      console.log(`🌍 CURRENT TIME DEBUG: UTC time: ${new Date().toUTCString()}`)
      console.log(`🌍 CURRENT TIME DEBUG: Local time in detected TZ: ${new Date().toLocaleString('en-US', { timeZone: detected })}`)
      return detected
    } catch {
      return 'America/New_York' // Fallback
    }
  })
  
  // Enhanced formatTime function with timezone support
  const formatTime = (timeStr: string, slot?: AppointmentSlot) => {
    console.log(`🎨 VISITS UI formatTime called with: ${timeStr}, slot:`, slot ? 'has slot data' : 'no slot')
    
    // PRIORITY: Use timezone-aware formatted time if available from getAvailableSlots
    if (slot?.formatted_start_time && timeStr === slot.slot_start_time) {
      console.log(`✅ Using timezone-converted start time: ${slot.formatted_start_time}`)
      return slot.formatted_start_time
    }
    if (slot?.formatted_end_time && timeStr === slot.slot_end_time) {
      console.log(`✅ Using timezone-converted end time: ${slot.formatted_end_time}`)
      return slot.formatted_end_time
    }
    
    // Also try display_start_time and display_end_time if formatted versions aren't available
    if (slot?.display_start_time && timeStr === slot.slot_start_time) {
      console.log(`🔄 Using display_start_time, formatting: ${slot.display_start_time}`)
      // Format the display time manually
      const timeParts = slot.display_start_time.split(':')
      const hours = parseInt(timeParts[0], 10)
      const minutes = parseInt(timeParts[1], 10)
      
      // Handle invalid time parts
      if (isNaN(hours) || isNaN(minutes)) {
        console.error(`❌ Invalid time format in display_start_time: ${slot.display_start_time}`)
        return slot.display_start_time // Return as-is if parsing fails
      }
      
      const period = hours >= 12 ? 'PM' : 'AM'
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
      return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`
    }
    if (slot?.display_end_time && timeStr === slot.slot_end_time) {
      console.log(`🔄 Using display_end_time, formatting: ${slot.display_end_time}`)
      // Format the display time manually
      const timeParts = slot.display_end_time.split(':')
      const hours = parseInt(timeParts[0], 10)
      const minutes = parseInt(timeParts[1], 10)
      
      // Handle invalid time parts
      if (isNaN(hours) || isNaN(minutes)) {
        console.error(`❌ Invalid time format in display_end_time: ${slot.display_end_time}`)
        return slot.display_end_time // Return as-is if parsing fails
      }
      
      const period = hours >= 12 ? 'PM' : 'AM'
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
      return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`
    }
    
    console.log(`⚠️ VISITS Falling back to basic formatting for: ${timeStr}`)
    // Fallback to basic formatting (this should NOT be used for timezone-converted slots)
    return new Date(`2000-01-01T${timeStr}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getTimezoneDisplay = () => {
    const shortName = patientTimezone.split('/').pop()?.replace('_', ' ') || 'Local'
    const offset = new Date().toLocaleString('en-US', {
      timeZone: patientTimezone,
      timeZoneName: 'short'
    }).split(' ').pop()
    return `${shortName} (${offset})`
  }

  const autoSelectNextAvailable = () => {
    if (timeSlots.length === 0) return
    
    // Select the first available time slot (they are already sorted chronologically)
    setSelectedTime(timeSlots[0].time)
  }
  
  // Track if appointment details have been changed for button state logic
  const appointmentChanged = React.useMemo(() => {
    if (!isRescheduleMode || !existingAppointment) return false
    
    const selectedDateStr = date?.toISOString().split('T')[0]
    const originalDateStr = existingAppointment.appointmentDate
    const originalTimeStr = existingAppointment.startTime.slice(0, 5)
    
    // Require either date OR time to be different from original appointment
    // This ensures user must select a different provider time slot
    const dateChanged = selectedDateStr !== originalDateStr
    const timeChanged = selectedTime !== originalTimeStr
    const hasChanged = dateChanged || timeChanged
    
    console.log('🔍 Appointment change check:', {
      selectedDate: selectedDateStr,
      originalDate: originalDateStr,
      selectedTime,
      originalTime: originalTimeStr,
      dateChanged,
      timeChanged,
      hasChanged
    })
    
    return hasChanged
  }, [isRescheduleMode, existingAppointment, date, selectedTime])

  // Create provider options for scheduling (show provider names)
  const medicationProviders = React.useMemo(() => {
    const combinations: Array<{ id: string; label: string; medicationId: string; providerId: string; treatmentType?: string; providerName: string }> = []
    const addedProviders = new Set<string>() // Track unique providers to avoid duplicates

    // Create options based on available providers
    providers.forEach(provider => {
      // Skip if we've already added this provider
      if (addedProviders.has(provider.id)) return

      // Find a medication preference that matches this provider's specialty/treatment type
      let matchingMed = medicationPreferences.find(med => {
        if (provider.treatment_type && med.treatment_type) {
          return provider.treatment_type === med.treatment_type
        }
        if (provider.specialty) {
          const specialtyToTreatment: Record<string, string> = {
            'Weight Loss': 'weight_loss',
            'Mens Health': 'mens_health'
          }
          return specialtyToTreatment[provider.specialty] === med.treatment_type
        }
        return false
      })

      // If no specific match, use the first medication preference
      if (!matchingMed && medicationPreferences.length > 0) {
        matchingMed = medicationPreferences[0]
      }

      if (matchingMed) {
        addedProviders.add(provider.id)
        combinations.push({
          id: `${matchingMed.id}-${provider.id}`,
          label: provider.name, // Show provider name
          medicationId: matchingMed.id,
          providerId: provider.id,
          treatmentType: matchingMed.treatment_type,
          providerName: provider.name
        })
      }
    })

    return combinations
  }, [medicationPreferences, providers])

  // Load available slots when date or medication/provider selection changes
  React.useEffect(() => {
    if (date && (selectedMedicationProvider || (isRescheduleMode && existingAppointment))) {
      console.log('🔄 RESCHEDULE SLOTS: Triggering slot load with:', {
        date: date?.toISOString().split('T')[0],
        isRescheduleMode,
        existingAppointment: existingAppointment?.id,
        patientTimezone
      })
      loadAvailableSlots()
    }
  }, [selectedMedicationProvider, date, isRescheduleMode, existingAppointment, patientTimezone])

  // Additional effect to ensure slots load immediately when reschedule dialog opens
  React.useEffect(() => {
    if (open && isRescheduleMode && existingAppointment && date) {
      console.log('🔄 RESCHEDULE DIALOG OPENED: Loading slots immediately')
      // Small delay to ensure state is fully set
      setTimeout(() => {
        loadAvailableSlots()
      }, 100)
    }
  }, [open, isRescheduleMode, existingAppointment, date])

  const loadAvailableSlots = async () => {
    if (!date) return
    
    // In reschedule mode, use existing appointment data
    if (isRescheduleMode && existingAppointment) {
      setLoading(true)
      // Preserve the existing selected time in reschedule mode
      const existingTime = existingAppointment.startTime.slice(0, 5)
      
      try {
        const startDate = date.toISOString().split('T')[0]
        const endDate = date.toISOString().split('T')[0] // Same day for now
        
        console.log('🔍 Reschedule mode - fetching slots for provider:', {
          providerId: existingAppointment.providerId,
          treatmentType: existingAppointment.treatmentType,
          date: startDate,
          preservingTime: existingTime
        })
        
        console.log(`🌍 VISITS TIMEZONE: Loading slots with patient timezone: ${patientTimezone}`)
        
        const result = await onGetAvailableSlots(
          existingAppointment.providerId,
          startDate,
          endDate,
          existingAppointment.treatmentType,
          patientTimezone
        )
        
        if (result.error) {
          console.error('Error loading slots:', result.error)
          Sonner.error('Loading slots', result.error.message || 'Failed to load available appointments')
          setAvailableSlots([])
        } else {
          const slots = result.data || []
          setAvailableSlots(slots)
          
          // Ensure the existing appointment time remains selected after slots load
          console.log('🔍 Slots loaded, preserving existing time selection:', existingTime)
          setSelectedTime(existingTime)
        }
      } catch (error) {
        console.error('Exception loading slots:', error)
        Sonner.error('Loading slots', 'An error occurred while loading appointments')
        setAvailableSlots([])
        // Still preserve the existing time even if slots fail to load
        setSelectedTime(existingTime)
      } finally {
        setLoading(false)
      }
      return
    }
    
    // Regular booking mode
    if (!selectedMedicationProvider) return

    const selectedCombo = medicationProviders.find(mp => mp.id === selectedMedicationProvider)
    if (!selectedCombo) return

    setLoading(true)
    setSelectedTime(null) // Reset selected time when slots change (only in booking mode)
    
    try {
      const startDate = date.toISOString().split('T')[0]
      const endDate = date.toISOString().split('T')[0] // Same day for now
      
      console.log(`🌍 VISITS TIMEZONE: Loading slots with patient timezone: ${patientTimezone}`)
      
      const result = await onGetAvailableSlots(
        selectedCombo.providerId,
        startDate,
        endDate,
        selectedCombo.treatmentType,
        patientTimezone
      )
      
      if (result.error) {
        console.error('Error loading slots:', result.error)
        Sonner.error('Loading slots', result.error.message || 'Failed to load available appointments')
        setAvailableSlots([])
      } else {
        setAvailableSlots(result.data || [])
      }
    } catch (error) {
      console.error('Exception loading slots:', error)
      Sonner.error('Loading slots', 'An error occurred while loading appointments')
      setAvailableSlots([])
    } finally {
      setLoading(false)
    }
  }

  const handleBookAppointment = async () => {
    if (!selectedTime || !selectedMedicationProvider || !date) return

    const selectedCombo = medicationProviders.find(mp => mp.id === selectedMedicationProvider)
    if (!selectedCombo) return

    setIsBooking(true)
    try {
      const result = await onBookAppointment({
        patientProfileId,
        providerId: selectedCombo.providerId,
        appointmentDate: date.toISOString().split('T')[0],
        startTime: selectedTime,
        treatmentType: selectedCombo.treatmentType || 'consultation',
        medicationPreferenceId: selectedCombo.medicationId,
        appointmentType: 'consultation',
        patientNotes: `Initial consultation for ${selectedCombo.label.split(' - ')[0]}`
      })
      
      if (result.success) {
        const providerName = selectedCombo.label.split(' - ')[1] || 'provider'
        Sonner.appointmentBooked(providerName)
        onOpenChange(false)
        // Reset form
        setSelectedTime(null)
        setSelectedMedicationProvider("")
        setDate(new Date())
      } else {
        Sonner.error('Booking appointment', result.message || 'Failed to book appointment')
      }
    } catch (error) {
      console.error('Exception booking appointment:', error)
      Sonner.error('Booking appointment', 'An error occurred while booking your appointment')
    } finally {
      setIsBooking(false)
    }
  }

  const handleRescheduleAppointment = async () => {
    if (!selectedTime || !date || !existingAppointment || !onRescheduleAppointment) return

    setIsBooking(true)
    try {
      const result = await onRescheduleAppointment({
        appointmentId: existingAppointment.id,
        appointmentDate: date.toISOString().split('T')[0],
        startTime: selectedTime
      })
      
      if (result.success) {
        // Create appointment details string for the toast using timezone-converted time
        const selectedSlot = timeSlots.find(slot => slot.time === selectedTime)
        const displayTime = selectedSlot?.displayTime || formatTime(selectedTime, undefined)
        
        const appointmentDetails = `${date?.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
        })} at ${displayTime}`
        
        console.log(`🌍 RESCHEDULE NOTIFICATION: Using timezone-converted time: ${displayTime} instead of provider time: ${selectedTime}`)
        
        Sonner.appointmentRescheduled(appointmentDetails)
        onOpenChange(false)
      } else {
        Sonner.error('Reschedule failed', result.message || 'Failed to reschedule appointment')
      }
    } catch (error) {
      console.error('Exception rescheduling appointment:', error)
      Sonner.error('Reschedule appointment', 'An error occurred while rescheduling your appointment')
    } finally {
      setIsBooking(false)
    }
  }


  // Generate time slots (9 AM to 5 PM, 15-minute intervals)
  const timeSlots = React.useMemo(() => {
    if (!date) return []
    
    // In reschedule mode, we don't need a provider selection to show time slots
    if (isRescheduleMode || selectedMedicationProvider) {
      const dateStr = date.toISOString().split('T')[0]
      const daySlots = availableSlots.filter(slot => slot.slot_date === dateStr)
      let slots = daySlots.map(slot => ({
        time: slot.slot_start_time.slice(0, 5), // Keep HH:MM format for comparison
        displayTime: formatTime(slot.slot_start_time, slot), // Use timezone-aware formatting
        originalSlot: slot
      }))
      
      // Filter out past time slots for current day in patient's timezone
      const today = new Date()
      const selectedDate = new Date(date)
      const isToday = selectedDate.toDateString() === today.toDateString()
      
      if (isToday && !isRescheduleMode) {
        const now = new Date()
        console.log(`🕐 TIME FIX 2025-10-29: Raw Date object: ${now.toString()}`)
        console.log(`🕐 TIME FIX 2025-10-29: UTC: ${now.toUTCString()}`)
        console.log(`🕐 TIME FIX 2025-10-29: Browser local: ${now.toLocaleString()}`)
        
        // Force correct timezone detection - PST is UTC-8, PDT is UTC-7
        const currentTimeInPatientTz = now.toLocaleTimeString('en-US', {
          timeZone: patientTimezone,
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        })
        
        const currentTimeInPatientTz12h = now.toLocaleTimeString('en-US', {
          timeZone: patientTimezone,
          hour12: true
        })
        
        // Additional debugging to understand the time discrepancy
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const systemTime = now.toLocaleTimeString('en-US', { hour12: false })
        
        console.log(`⏰ TIME FIX DEBUG: Browser timezone detected: ${browserTimezone}`)
        console.log(`⏰ TIME FIX DEBUG: System time: ${systemTime}`)
        console.log(`⏰ TIME FIX DEBUG: Patient timezone ${patientTimezone}`)
        console.log(`⏰ TIME FIX DEBUG: Current time in patient TZ (24h): ${currentTimeInPatientTz}`)
        console.log(`⏰ TIME FIX DEBUG: Current time in patient TZ (12h): ${currentTimeInPatientTz12h}`)
        
        // Check if the browser time is correct
        const utcHour = now.getUTCHours()
        const utcMinute = now.getUTCMinutes()
        console.log(`⏰ TIME FIX DEBUG: UTC time is ${utcHour}:${utcMinute.toString().padStart(2, '0')}`)
        
        // PST is UTC-8, so if it's 10 AM PST, UTC should be 18:00 (6 PM)
        // PDT is UTC-7, so if it's 10 AM PDT, UTC should be 17:00 (5 PM)
        const expectedUTCForPST = (10 + 8) % 24  // 18:00 UTC
        const expectedUTCForPDT = (10 + 7) % 24  // 17:00 UTC
        console.log(`⏰ TIME FIX DEBUG: If it's 10 AM PST, UTC should be ${expectedUTCForPST}:xx`)
        console.log(`⏰ TIME FIX DEBUG: If it's 10 AM PDT, UTC should be ${expectedUTCForPDT}:xx`)
        
        slots = slots.filter(slot => {
          // For current day, only show slots that haven't passed in patient's timezone
          if (slot.originalSlot?.display_start_time) {
            // Use the timezone-converted display time for comparison
            const slotTime = slot.originalSlot.display_start_time
            const isSlotInFuture = slotTime > currentTimeInPatientTz
            console.log(`⏰ SLOT FILTER: ${slot.displayTime} (${slotTime}) vs current ${currentTimeInPatientTz} = ${isSlotInFuture ? 'SHOW' : 'HIDE'}`)
            return isSlotInFuture
          } else {
            // Fallback: compare using basic time parsing
            const slotTime = slot.time
            const isSlotInFuture = slotTime > currentTimeInPatientTz.slice(0, 5)
            console.log(`⏰ SLOT FILTER FALLBACK: ${slot.displayTime} (${slotTime}) vs current ${currentTimeInPatientTz} = ${isSlotInFuture ? 'SHOW' : 'HIDE'}`)
            return isSlotInFuture
          }
        })
        
        console.log(`⏰ FILTERED RESULT: Showing ${slots.length} future slots for today`)
      }
      
      // In reschedule mode, exclude the current appointment time from available slots
      if (isRescheduleMode && existingAppointment) {
        const existingTime = existingAppointment.startTime.slice(0, 5)
        slots = slots.filter(slot => slot.time !== existingTime)
        console.log('🔍 Removed existing appointment time from available slots:', existingTime)
      }
      
      return slots.sort((a, b) => a.time.localeCompare(b.time)) // Sort times chronologically
    }
    
    return []
  }, [availableSlots, date, isRescheduleMode, selectedMedicationProvider, existingAppointment, patientTimezone])

  const selectedMedicationProviderData = medicationProviders.find(mp => mp.id === selectedMedicationProvider)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="px-6 pt-6 pb-4 md:px-6 md:pt-4 md:pb-3">
          <DialogTitle className="md:text-xl">
            {isRescheduleMode ? 'Reschedule Your Visit' : 'Schedule Your Visit'}
          </DialogTitle>
          <DialogDescription className="md:text-base">
            {isRescheduleMode 
              ? `Modify your ${existingAppointment?.formattedDate || existingAppointment?.appointmentDate || ''} appointment at ${(() => {
                  // Format existing appointment time in patient's timezone
                  if ((existingAppointment as any)?.displayTime) {
                    return (existingAppointment as any).displayTime
                  }
                  if (existingAppointment?.startTime) {
                    console.log(`🔄 RESCHEDULE DIALOG: Converting existing appointment time ${existingAppointment.startTime} to patient timezone`)
                    try {
                      // Convert provider timezone time to patient timezone
                      const appointmentDate = existingAppointment.appointmentDate || new Date().toISOString().split('T')[0]
                      const providerDateTime = new Date(`${appointmentDate}T${existingAppointment.startTime}`)
                      
                      // Create a properly formatted time string in the patient's timezone
                      const patientTime = providerDateTime.toLocaleString('en-US', {
                        timeZone: patientTimezone,
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }).split(' ')
                      
                      const timeOnly = `${patientTime[0]} ${patientTime[1]}`
                      console.log(`✅ RESCHEDULE DIALOG: Converted ${existingAppointment.startTime} to patient timezone: ${timeOnly}`)
                      return timeOnly
                    } catch (error) {
                      console.error('Error converting existing appointment time to patient timezone:', error)
                      // Fallback to basic formatting
                      return formatTime(existingAppointment.startTime, undefined)
                    }
                  }
                  return ''
                })()} with ${existingAppointment?.providerName || 'your provider'}.`
              : 'Book your appointment with your healthcare provider.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <Card className="gap-0 p-0 md:max-w-[700px] lg:max-w-[800px] flex flex-col mx-auto border-0 shadow-none">
          <CardContent className="relative p-0 md:pr-64 flex-1 min-h-0">
            <div className="p-6">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                disabled={bookedDates}
                showOutsideDays={false}
                modifiers={{
                  booked: bookedDates,
                }}
                modifiersClassNames={{
                  booked: "[&>button]:line-through opacity-100",
                }}
                className="bg-transparent p-0 w-full [--cell-size:--spacing(12)] md:[--cell-size:--spacing(14)] [&_table]:w-full [&_td]:text-center [&_th]:text-center [&_table]:table-fixed"
                formatters={{
                  formatWeekdayName: (date) => {
                    return date.toLocaleString("en-US", { weekday: "short" })
                  },
                }}
              />
            </div>
            <div className="no-scrollbar inset-y-0 right-0 flex max-h-96 w-full scroll-pb-6 flex-col border-t p-6 md:absolute md:max-h-none md:w-64 md:border-t-0 md:border-l">
              {/* Provider Selection - Hide in reschedule mode */}
              {!isRescheduleMode && (
                <div className={`flex flex-col gap-3 ${selectedMedicationProvider ? 'pb-4 border-b' : ''}`}>
                  <label className="text-sm font-medium">Select Provider</label>
                  {medicationProviders.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center p-4 border border-dashed rounded-md">
                      No providers available. Contact support to be assigned to a healthcare provider.
                    </div>
                  ) : (
                    <Select 
                      value={selectedMedicationProvider} 
                      onValueChange={setSelectedMedicationProvider}
                      onOpenChange={(open) => {
                        // Focus trap for select when it opens
                        if (open) {
                          setTimeout(() => {
                            const selectTrigger = document.querySelector('[data-state="open"] [role="combobox"]')
                            if (selectTrigger && selectTrigger instanceof HTMLElement) {
                              selectTrigger.focus()
                            }
                          }, 50)
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select provider..." />
                      </SelectTrigger>
                      <SelectContent>
                        {medicationProviders.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              
              {/* Available Times Section - takes up remaining space */}
              <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
                {(isRescheduleMode || selectedMedicationProvider) ? (
                  <>
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                      <span>Times shown in your timezone: {getTimezoneDisplay()}</span>
                    </div>
                    {loading ? (
                      <div className="text-sm text-muted-foreground text-center">Loading times...</div>
                    ) : timeSlots.length === 0 ? (
                      <div className="text-sm text-muted-foreground text-center">
                        No available times
                      </div>
                    ) : (
                      <>
                        {/* Next Available Button */}
                        <div className="flex justify-center pb-3">
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={autoSelectNextAvailable}
                            className="flex items-center gap-2"
                          >
                            <Clock className="h-4 w-4" />
                            Next Available
                          </Button>
                        </div>
                        
                        <div className="grid gap-2">
                          {timeSlots.map((slotData) => (
                            <Button
                              key={slotData.time}
                              variant={selectedTime === slotData.time ? "default" : "outline"}
                              onClick={() => setSelectedTime(slotData.time)}
                              className="w-full shadow-none"
                              size="sm"
                            >
                              {slotData.displayTime}
                            </Button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground text-center">
                    No provider selected
                  </div>
                )}
              </div>
              
              {/* Action Buttons - Fixed to bottom */}
              <div className="pt-4 border-t mt-auto space-y-2">
                {isRescheduleMode ? (
                  <Button
                    disabled={!date || !selectedTime || isBooking || !appointmentChanged}
                    className="w-full"
                    onClick={handleRescheduleAppointment}
                  >
                    {isBooking ? "Rescheduling..." : "Reschedule Appointment"}
                  </Button>
                ) : (
                  <Button
                    disabled={!date || !selectedTime || !selectedMedicationProvider || isBooking}
                    className="w-full"
                    onClick={handleBookAppointment}
                  >
                    {isBooking ? "Booking..." : "Book Visit"}
                  </Button>
                )}
                
                {date && selectedTime && (isRescheduleMode || selectedMedicationProviderData) && (
                  <div className="text-xs text-muted-foreground text-center mt-2">
                    {date?.toLocaleDateString("en-US", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}{" "}
                    at {(() => {
                      // Find the selected slot to get the timezone-converted display time
                      const selectedSlot = timeSlots.find(slot => slot.time === selectedTime)
                      if (selectedSlot?.displayTime) {
                        console.log(`📅 CONFIRMATION: Using timezone-converted time: ${selectedSlot.displayTime} instead of original: ${selectedTime}`)
                        return selectedSlot.displayTime
                      }
                      // Fallback to basic formatting if no slot data available
                      console.log(`⚠️ CONFIRMATION: No converted time found, using fallback for: ${selectedTime}`)
                      return formatTime(selectedTime, undefined)
                    })()}
                    {!isRescheduleMode && selectedMedicationProviderData && (
                      <> with {selectedMedicationProviderData.label}</>
                    )}
                    {isRescheduleMode && existingAppointment && (
                      <> with {existingAppointment.providerName}</>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  )
}